<template>
  <div 
    id="timeline" 
    :style="timelineStyles">
    <label 
      id="open-file-button" 
      for="open-file-input" 
      class="icon-holder">

      <svg 
        id="open-file-icon" 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 32 32"
        class="icon">
        <rect
          width="32" 
          height="32"/>
        <path d="M24,16H23v5H9V16H8a2,2,0,0,0-2,2v4a2,2,0,0,0,2,2H24a2,2,0,0,0,2-2V18A2,2,0,0,0,24,16Z"/>
        <polygon points="21 13 16 7 11 13 14 13 14 19 18 19 18 13 21 13"/>
      </svg>
      <input 
        id="open-file-input" 
        type="file" 
        accept=".json,.jpg" 
        multiple 
        @change.stop="handleFileUpload">
    </label>

    <div 
      id="events-wrapper" 
      ref="events-wrapper"
      :style="{ 'width': eventsWrapperWidth + 'px' }">
      <div 
        :style="{ 'right': leftOverlayRight + 'px' }"
        class="overlay" />

      <div 
        id="left-bracket"
        ref="left-bracket"
        :class="{ 'hidden': !filteredEvents.length }"
        :style="{ 'left': leftBracketLeft + 'px' }"
        class="bracket h-dragable"
        @mousedown.prevent="leftBracketBeingDragged = true">
        <div class="bracket-inner"/>
      </div>

      <ul 
        id="events-list" 
        :style="{ 'width': eventsWrapperWidth + 'px' }">
        <li 
          v-for="(event, index) in events"
          :key="event.key"
          :id="'event-' + event.time"
          :style="eventStyles(event)"
          :class="{
            'passed': isPassed(event, activeEventNr),
            'active': isActive(event, activeEventNr),
            'left-mouse': event.event.indexOf('LB') >= 0,
            'middle-mouse': event.event.indexOf('MB') >= 0,
            'right-mouse': event.event.indexOf('RB') >= 0
          }"
          class="event">
          <div 
            v-if="index > 0" 
            :class="eventTimeBarClass(event)"
            class="event-time-bar"/>
          <div
            :class="getEventType(event)"
            class="event-icon clickable"
            @click="selectEvent(event)"/>
          <div class="cursor"/>
        </li>
      </ul>

      <div 
        id="right-bracket"
        ref="right-bracket"
        :class="{ 'hidden': !filteredEvents.length }"
        :style="{ 'right': rightBracketRight + 'px' }"
        class="bracket h-dragable"
        @mousedown.prevent="rightBracketBeingDragged = true">
        <div class="bracket-inner"/>
      </div>

      <div 
        :style="{ 'left': rightOverlayLeft + 'px' }"
        class="overlay" />
    </div>

    <div 
      id="info"
      :class="{ 'hidden': !filteredEvents.length }">
      <div 
        id="info-data"
        ref="info-data"
        class="info-pane"
        @click="copy('info-data')">
        <strong id="duration">{{ totalTime ? formatTime(totalTime) : '00:00' }}</strong>
        <strong id="friction">{{ formatNumber(uxf, 0) }}</strong>
      </div>

      <button 
        id="snapshot-button"
        ref="snapshot-button"
        :class="{ 'hidden': !filteredEvents.length }"
        class="icon-holder"
        @click="snapshot">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          xmlns:xlink="http://www.w3.org/1999/xlink" 
          viewBox="0 0 128 110">
          <image 
            width="128" 
            height="110" 
            xlink:href="data:image/png;base64,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"/>
          <path d="M94.56,26h-8a31.86,31.86,0,0,0-37.22,0h-16A9.47,9.47,0,0,0,24,35.44V72.56A9.47,9.47,0,0,0,33.44,82H94.56A9.47,9.47,0,0,0,104,72.56V35.44A9.47,9.47,0,0,0,94.56,26ZM36,42a4,4,0,1,1,4-4A4,4,0,0,1,36,42ZM68,76A24,24,0,1,1,92,52,24,24,0,0,1,68,76Z"/>
          <path d="M68,71.55A19.55,19.55,0,1,1,87.55,52,19.57,19.57,0,0,1,68,71.55Zm0-32A12.45,12.45,0,1,0,80.45,52,12.47,12.47,0,0,0,68,39.55Z"/>
        </svg>
      </button>
    </div>

    <a 
      id="download-button"
      :href="recordDownloadURL"
      :class="{ 'show-guide': !downloadTriggered }"
      class="icon-holder"
      @click="addDownloadEvent(recordDownloadURL)">
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 19.5 19.5" 
        class="icon">
        <polygon points="4.75 9.75 9.75 15.75 14.75 9.75 11.75 9.75 11.75 4.75 7.75 4.75 7.75 9.75 4.75 9.75"/>
        <circle 
          cx="9.75" 
          cy="9.75" 
          r="8.5"/>
      </svg>
    </a>

    <textarea 
      ref="copy-value"
      class="copy-value"/>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import * as cst from '../lib/constants'
import { getEventType, isPassed, isActive, formatTime, formatNumber } from '../lib/functions'
import stats from '../lib/stats/stats'
import * as evts from '../lib/stats/event-types'
import { setCookie, getCookie } from '../lib/cookies/cookies'

const downloadTriggeredCookieName = 'downloadTriggered'

const stepSize = 500
const bracketMove = cmp => keyEvent => {
  switch (true) {
    case keyEvent.key === '1' && cmp.minTime - stepSize > 0:
      cmp.setMinTime(cmp.minTime - stepSize)
      break
    case keyEvent.key === '2' && cmp.activeEvent && cmp.minTime + stepSize <= cmp.activeEvent.time:
      cmp.setMinTime(cmp.minTime + stepSize)
      break
    case keyEvent.key === '9' && cmp.activeEvent && cmp.maxTime - stepSize >= cmp.activeEvent.time:
      cmp.setMaxTime(cmp.maxTime - stepSize)
      break
    case keyEvent.key === '0' && cmp.events && cmp.maxTime + stepSize <= cmp.events[cmp.events.length - 1].time:
      cmp.setMaxTime(cmp.maxTime + stepSize)
      break
  }
}

const mouseMove = cmp => mouseMoveEvent => {
  if (cmp.leftBracketBeingDragged) {
    cmp.leftBracketDragEvent = mouseMoveEvent
  } else if (cmp.rightBracketBeingDragged) {
    cmp.rightBracketDragEvent = mouseMoveEvent
  }
}

const mouseUp = cmp => () => {
  if (cmp.leftBracketBeingDragged) {
    cmp.dropLeftBracket()
  } else if (cmp.rightBracketBeingDragged) {
    cmp.dropRightBracket()
  }
}

const resize = cmp => {
  return debounce(() => {
    cmp.windowWidth = window.innerWidth
  })
}

export default {
  name: 'Timeline',
  data() {
    return {
      windowWidth: window.innerWidth,
      leftBracketBeingDragged: false,
      leftBracketDragEvent: null,
      rightBracketBeingDragged: false,
      rightBracketDragEvent: null,
      cst,
      baseUrl: process.env.BASE_URL,
      downloadTriggered: this.getCookie(downloadTriggeredCookieName) === 'true',
    }
  },
  computed: {
    ...mapState(['recordDownloadURL', 'events']),
    ...mapGetters([
      'relativeHeight',
      'filteredEvents',
      'activeEventNr',
      'activeEvent',
      'uxf',
      'minTime',
      'maxTime',
      'totalTime',
    ]),
    timelineStyles() {
      return {
        height: this.cst.TIMELINE_HEIGHT + 'px',
        top: this.relativeHeight + 'px',
      }
    },
    eventsWrapperWidth() {
      return this.windowWidth - 275
    },
    leftBracketLeft() {
      const eventsWrapper = this.$refs['events-wrapper']

      if (!this.events || !this.events.length || !eventsWrapper) {
        return 0
      }

      const eventsWrapperRect = eventsWrapper.getBoundingClientRect()
      const cursor = eventsWrapper.querySelector('#event-' + this.activeEvent.time + ' .cursor')

      if (this.leftBracketBeingDragged && this.leftBracketDragEvent && cursor) {
        const newX = this.leftBracketDragEvent.clientX
        const minX = eventsWrapperRect.left
        const maxX = cursor.getBoundingClientRect().left

        if (newX <= minX) {
          return minX - eventsWrapperRect.left
        } else if (newX >= maxX) {
          return maxX - eventsWrapperRect.left
        }
        return newX - eventsWrapperRect.left
      } else {
        const totalTime = this.events[this.events.length - 1].time
        return this.eventsWrapperWidth * (this.minTime / totalTime)
      }
    },
    rightBracketRight() {
      const eventsWrapper = this.$refs['events-wrapper']

      if (!this.events || !this.events.length || !eventsWrapper) {
        return 0
      }

      const eventsWrapperRect = eventsWrapper.getBoundingClientRect()
      const cursor = eventsWrapper.querySelector('#event-' + this.activeEvent.time + ' .cursor')

      if (this.rightBracketBeingDragged && this.rightBracketDragEvent && cursor) {
        const newX = this.rightBracketDragEvent.clientX
        const minX = cursor.getBoundingClientRect().right
        const maxX = eventsWrapperRect.right

        if (newX <= minX) {
          return this.eventsWrapperWidth - (minX - eventsWrapperRect.left)
        } else if (newX >= maxX) {
          return this.eventsWrapperWidth - (maxX - eventsWrapperRect.left)
        }
        return this.eventsWrapperWidth - (newX - eventsWrapperRect.left)
      } else {
        const totalTime = this.events[this.events.length - 1].time
        return this.eventsWrapperWidth - this.eventsWrapperWidth * (this.maxTime / totalTime)
      }
    },
    leftOverlayRight() {
      return this.eventsWrapperWidth - this.leftBracketLeft
    },
    rightOverlayLeft() {
      return this.eventsWrapperWidth - this.rightBracketRight
    },
  },
  created() {
    window.addEventListener('keydown', bracketMove(this))
    window.addEventListener('mousemove', mouseMove(this))
    window.addEventListener('mouseup', mouseUp(this))
    window.addEventListener('resize', resize(this))
  },
  destroyed() {
    window.removeEventListener('keydown', bracketMove(this))
    window.removeEventListener('mousemove', mouseMove(this))
    window.removeEventListener('mouseup', mouseUp(this))
    window.removeEventListener('resize', resize(this))
  },
  methods: {
    ...mapActions(['handleFileUpload']),
    getEventType,
    isPassed,
    isActive,
    formatTime,
    formatNumber,
    setCookie,
    getCookie,
    eventStyles(event) {
      const eventIndex = this.events.indexOf(event)
      const previousEvent = this.events[eventIndex - 1]
      const previousEventTime = previousEvent ? previousEvent.time : 0
      const totalTime = this.events[this.events.length - 1].time
      const eventTime = event.time - previousEventTime
      const eventWidth = (eventTime / totalTime) * 100
      const eventStyles = {
        width: eventWidth + '%',
      }
      if (eventIndex <= 1) {
        eventStyles['left'] = 0
      }
      return eventStyles
    },
    eventTimeBarClass(event) {
      const eventIndex = this.events.indexOf(event)
      const previousEvent = this.events[eventIndex - 1]
      if (eventIndex > 1 && previousEvent && previousEvent.event.indexOf('START') >= 0) {
        return 'dotted'
      }
    },
    setMinTime(minTime) {
      const query = { ...this.$route.query }
      query.minTime = Math.floor(minTime)
      this.$router.push({ name: 'event', query })
    },
    setMaxTime(maxTime) {
      const query = { ...this.$route.query }
      query.maxTime = Math.ceil(maxTime)
      this.$router.push({ name: 'event', query })
    },
    dropLeftBracket() {
      const eventsWrapper = this.$refs['events-wrapper']

      if (!this.leftBracketBeingDragged || !this.leftBracketDragEvent || !eventsWrapper) {
        return
      }

      const eventsWrapperRect = eventsWrapper.getBoundingClientRect()
      const totalTime = this.events[this.events.length - 1].time
      const totalWidth = eventsWrapperRect.right - eventsWrapperRect.left
      const relativeX = this.leftBracketDragEvent.clientX - eventsWrapperRect.left
      const minTime = totalTime * (relativeX / totalWidth)

      if (minTime <= 0) {
        this.setMinTime(0)
      } else if (minTime >= this.activeEvent.time) {
        this.setMinTime(this.activeEvent.time)
      } else {
        this.setMinTime(minTime)
      }

      this.leftBracketBeingDragged = false
      this.leftBracketDragEvent = null
    },
    dropRightBracket() {
      const eventsWrapper = this.$refs['events-wrapper']

      if (!this.rightBracketBeingDragged || !this.rightBracketDragEvent || !eventsWrapper) {
        return
      }

      const eventsWrapperRect = eventsWrapper.getBoundingClientRect()
      const totalTime = this.events[this.events.length - 1].time
      const totalWidth = eventsWrapperRect.right - eventsWrapperRect.left
      const ratioOfWidth = this.rightBracketRight / totalWidth
      const maxTime = totalTime - ratioOfWidth * totalTime

      if (maxTime >= totalTime) {
        this.setMaxTime(totalTime)
      } else if (maxTime <= this.activeEvent.time) {
        this.setMaxTime(this.activeEvent.time + 1)
      } else {
        this.setMaxTime(maxTime)
      }

      this.rightBracketBeingDragged = false
      this.rightBracketDragEvent = null
    },
    selectEvent(event) {
      const query = { ...this.$route.query }
      this.$router.push({ name: 'event', params: { eventNr: event.key }, query })
    },
    setElementInUse(el) {
      const inUseClass = ' in-use'
      el.className += inUseClass
      setTimeout(() => {
        el.className = el.className.substring(0, el.className.length - inUseClass.length)
      }, 800)
    },
    copy(ref) {
      const el = this.$refs[ref]

      this.setElementInUse(el)

      const copyEl = this.$refs['copy-value']
      copyEl.value = el.innerText.replace(/,/g, '')
      copyEl.focus()
      copyEl.select()
      document.execCommand('copy')
    },
    snapshot() {
      const el = this.$refs['snapshot-button']
      this.setElementInUse(el)
      this.$eventBus.$emit('take-snapshot')
    },
    addDownloadEvent(file) {
      this.setCookie(downloadTriggeredCookieName)
      this.downloadTriggered = true
      if (file.indexOf('exe') >= 0) {
        stats.addEvent(evts.RECORDER_WIN_DOWNLOADED)
      } else if (file.indexOf('dmg') >= 0) {
        stats.addEvent(evts.RECORDER_MAC_DOWNLOADED)
      }
    },
  },
}
</script>

<style lang="scss">
#timeline {
  position: absolute;
  width: 100%;
  display: grid;
  grid-template-areas: 'open events info download';
  grid-template-columns: 32px auto 211px 32px;
  grid-template-rows: 32px;
  place-items: center start;
  background: #e5e5e5;

  #open-file-button {
    grid-area: open;
  }

  #events-wrapper {
    grid-area: events;
    position: relative;
    height: 100%;
    overflow: hidden;
  }

  #info {
    grid-area: info;
    height: 100%;
    width: 210px;
  }

  #download-button {
    grid-area: download;
    padding: 6px;
    position: relative;
  }

  #download-button svg circle {
    fill: none;
    stroke: #4a4e54;
    stroke-width: 3px;
  }

  #download-button:hover svg circle {
    stroke: #222;
  }

  #download-button::after {
    content: 'DOWN\A LOAD';
    font-size: 82%;
    top: -25px;
  }

  #download-button.show-guide::before {
    content: '';
    background: url(../assets/1ST_Download_Record.svg) no-repeat;
    width: 250px;
    height: 200px;
    display: block;
    position: absolute;
    left: -207px;
    top: -137px;
  }

  #events-wrapper,
  #info {
    background: #979797;
  }

  #events-wrapper,
  .overlay,
  #info {
    box-shadow: inset 0 7px 8px 0 rgba(0, 0, 0, 0.24);
  }

  .overlay {
    background-color: #c0c0c0;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    z-index: 1;
  }

  .bracket {
    z-index: 100;
    position: absolute;
    top: 0;
    height: 100%;
  }

  .bracket .bracket-inner {
    background: #53585f;
    width: 2px;
    height: 100%;
  }

  .bracket:hover {
    padding: 0 15px;
    width: 4px;
  }

  #left-bracket::before,
  #left-bracket::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 8px solid #53585f;
    display: block;
    position: absolute;
    right: -8px;
  }

  #left-bracket::before {
    border-top: 0 solid transparent;
    border-bottom: 5px solid transparent;
    top: 0;
  }

  #left-bracket::after {
    border-top: 6px solid transparent;
    border-bottom: 0 solid transparent;
    bottom: 0;
  }

  #left-bracket:hover {
    margin: 0 0 0 -15px;
  }

  #left-bracket:hover::before,
  #left-bracket:hover::after {
    right: 8px;
  }

  #right-bracket::before,
  #right-bracket::after {
    content: '';
    width: 0;
    height: 0;
    border-right: 8px solid #53585f;
    display: block;
    position: absolute;
    left: -8px;
  }

  #right-bracket::before {
    border-top: 0 solid transparent;
    border-bottom: 5px solid transparent;
    top: 0;
  }

  #right-bracket::after {
    border-top: 6px solid transparent;
    border-bottom: 0 solid transparent;
    bottom: 0;
  }

  #right-bracket:hover {
    margin: 0 -16px 0 0;
  }

  #right-bracket:hover::before,
  #right-bracket:hover::after {
    left: 6px;
  }

  .bracket:hover .bracket-inner {
    width: 4px;
    margin-left: -1px;
    box-shadow: 1px 1px 10px #333;
    transition: width, margin-left, box-shadow 0.2s ease-out;
  }

  #events-list {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 50;
    padding: 0;
    margin: 0;
  }

  .event {
    position: relative;
    display: block;
    float: left;
    height: 32px;
  }

  .event-time-bar {
    border: 3px solid #0073ff;
    width: 100%;
    position: absolute;
    right: 0;
    top: 50%;
    margin-top: -3px;
  }

  .event-time-bar.dotted {
    border-style: dotted;
  }

  .passed .event-time-bar {
    border-color: #ff00a4;
  }

  .active .event-time-bar {
    border-color: #000;
  }

  .event-icon {
    background: #fff;
    border: 2px solid #0073ff;
    width: 12px;
    height: 12px;
    position: absolute;
    right: -6px;
    z-index: 90;
    top: 50%;
    margin-top: -8px;
  }

  .event-icon.start {
    left: 0;
  }

  .event-icon.stop {
    right: 0;
  }

  .passed .event-icon {
    border-color: #ff00a4;
  }

  .active .event-icon {
    border-color: #000;
  }

  .event-icon.start,
  .event-icon.stop,
  .event-icon.drag-start,
  .event-icon.drag-end,
  .event-icon.scroll-start,
  .event-icon.scroll-end {
    background: none;
    width: 0;
    height: 0;
  }

  .event-icon.drag-start,
  .event-icon.drag-end {
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
  }

  .event-icon.start,
  .event-icon.stop,
  .event-icon.scroll-start,
  .event-icon.scroll-end {
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
  }

  .event-icon.start::before,
  .event-icon.stop::before,
  .event-icon.drag-start::before,
  .event-icon.drag-end::before,
  .event-icon.scroll-end::before,
  .event-icon.scroll-start::before {
    content: '';
    display: block;
    position: absolute;
    width: 0;
    height: 0;
  }

  .event-icon.drag-start::before,
  .event-icon.drag-end::before {
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
  }

  .event-icon.start::before,
  .event-icon.stop::before,
  .event-icon.scroll-end::before,
  .event-icon.scroll-start::before {
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    top: -6px;
  }

  .event-icon.start,
  .event-icon.scroll-end {
    border-left: 15px solid #0073ff;
    border-right: none;
  }

  .event-icon.drag-end {
    border-bottom: 15px solid #0073ff;
    border-top: none;
  }

  .event-icon.start::before,
  .event-icon.scroll-end::before {
    border-left: 9px solid #fff;
    left: -13px;
  }

  .event-icon.drag-end::before {
    border-bottom: 9px solid #fff;
    top: 4px;
    left: -6px;
  }

  .passed .event-icon.start,
  .passed .event-icon.scroll-end {
    border-left-color: #ff00a4;
  }

  .active .event-icon.start,
  .active .event-icon.scroll-end {
    border-left-color: #000;
  }

  .passed .event-icon.drag-end {
    border-bottom-color: #ff00a4;
  }

  .active .event-icon.drag-end {
    border-bottom-color: #000;
  }

  .event-icon.stop,
  .event-icon.scroll-start {
    border-right: 15px solid #0073ff;
    border-left: none;
  }

  .event-icon.drag-start {
    border-top: 15px solid #0073ff;
    border-bottom: none;
  }

  .event-icon.stop::before,
  .event-icon.scroll-start::before {
    border-right: 9px solid #fff;
    right: -13px;
  }

  .event-icon.drag-start::before {
    border-top: 9px solid #fff;
    top: -13px;
    left: -6px;
  }

  .passed .event-icon.stop,
  .passed .event-icon.scroll-start {
    border-right-color: #ff00a4;
  }

  .active .event-icon.stop,
  .active .event-icon.scroll-start {
    border-right-color: #000;
  }

  .passed .event-icon.drag-start {
    border-top-color: #ff00a4;
  }

  .active .event-icon.drag-start {
    border-top-color: #000;
  }

  .event-icon.click,
  .event-icon.d-click {
    border-radius: 20px;
  }

  .event-icon.d-click::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 15px;
    margin: 2px 0 0 2px;
    background: #0073ff;
  }

  .passed .event-icon.d-click::before {
    background-color: #ff00a4;
  }

  .active .event-icon.d-click::before {
    background-color: #000;
  }

  .event::after {
    display: block;
    position: absolute;
    color: #000;
    font-size: 8pt;
    top: 0;
    right: -12px;
  }

  .middle-mouse::after {
    content: 'M';
  }

  .right-mouse::after {
    content: 'R';
  }

  .cursor {
    visibility: hidden;
    position: absolute;
    right: 1px;
    height: 100%;
    border-right: 1px solid #fff;
    z-index: 100;
  }

  .active .cursor {
    visibility: visible;
  }

  #open-file-input {
    display: none;
  }

  #open-file-button {
    width: 100%;
    height: 100%;
  }

  #open-file-button rect {
    fill: none;
  }

  #open-file-button::after {
    content: 'OPEN';
  }

  #info {
    display: grid;
    grid-template-areas: 'info-data . snapshot';
    grid-template-columns: 150px auto 32px;
    grid-template-rows: 32px;
    place-items: center end;
  }

  #info-data {
    grid-area: info-data;
    color: #fff;
    text-shadow: #000 0px 1.3px 2px;
    display: grid;
    grid-template-areas: 'duration friction';
    grid-template-columns: 60px 90px;
    grid-template-rows: 32px;
    place-items: center end;
    padding-right: 60px;
    margin-right: -60px;
  }

  #info-data:hover {
    background: url(../assets/info-pane-bg.jpg) 10px 6px no-repeat;
    color: #53585f;
    text-shadow: none;
  }

  #info-data::after {
    left: 45%;
    top: -8px;
  }

  #duration {
    grid-area: duration;
  }

  #friction {
    grid-area: friction;
  }

  #duration,
  #friction {
    position: relative;
  }

  #duration:after,
  #friction:after {
    position: absolute;
    top: 4px;
    right: -22px;
    font-size: 75%;
    font-weight: normal;
  }

  #duration:after {
    content: 'dur';
  }

  #friction:after {
    content: 'uxf';
  }

  #snapshot-button {
    grid-area: snapshot;
    width: 100%;
    height: 100%;
    padding: 3px 0;
  }

  #snapshot-button svg path {
    fill: #fff;
    box-shadow: #333 0 1px 1px;
  }

  #snapshot-button:hover svg image {
    visibility: hidden;
  }

  #snapshot-button:hover svg path {
    fill: #222;
  }

  #snapshot-button::after {
    content: 'SHOT';
    padding: 0;
    font-size: 7.5pt;
    top: -9px;
  }

  #snapshot-button.in-use::after {
    content: 'SAVED';
  }
}
</style>
